import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";
import PublicNavBar from "../components/PublicNavBar";
import PublicFooter from "../components/PublicFooter";
import StickyApplyPanel from "../components/opportunities/StickyApplyPanel";
import JobCard from "../components/JobCard";
import {
  MapPin, Briefcase, Clock, Building2, ShieldCheck, ArrowLeft, Calendar,
  IndianRupee, Users, Sparkles,
} from "lucide-react";

const SC_TINT = {
  SMPL: "#F59E0B", EVFM: "#10B981", MEDL: "#DC2626", AIOF: "#9333EA",
  SMIA: "#0EA5E9", CIDSA: "#16A34A", ESDM: "#F97316", "3CI": "#EF4444",
  READ: "#0891B2", "IoT-CPS": "#7C3AED", OTHER: "#64748B",
};

function timeAgo(value) {
  if (!value) return "";
  try {
    const ms = typeof value === "number" ? value * 1000 : Date.parse(value);
    if (!ms || isNaN(ms)) return "";
    const diff = Math.floor((Date.now() - ms) / 1000);
    if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
    return `${Math.floor(diff / 2592000)}mo ago`;
  } catch { return ""; }
}

function CompanyLogo({ src, name, size = 72 }) {
  const [broken, setBroken] = useState(false);
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  if (src && !broken) {
    return (
      <img
        src={src} alt={name} loading="lazy"
        onError={() => setBroken(true)}
        className="rounded-md object-contain bg-white border border-rule shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-md border border-rule grid place-items-center bg-slate-50 shrink-0"
      style={{ width: size, height: size }}
    >
      <span className="text-xl font-bold text-slate-400">{initial}</span>
      <span className="sr-only">{name}</span>
    </div>
  );
}

function Section({ id, eyebrow, children, testid }) {
  return (
    <section
      id={id}
      className="bg-white border border-rule rounded-xl p-5 sm:p-6 scroll-mt-24"
      data-testid={testid || `section-${id}`}
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-signal mb-3">{eyebrow}</div>
      {children}
    </section>
  );
}

export default function JobDetailPublic() {
  const { pid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);
  const [applied, setApplied] = useState(false);
  const [applying, setApplying] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get(`/postings/${pid}`)
      .then(r => setP(r.data))
      .catch(() => setErr("This job posting is not available."));
  }, [pid]);

  useEffect(() => {
    if (!user || user.role !== "student") return;
    api.get("/me/applications").then(r => {
      if (r.data.some(a => a.posting_id === pid)) setApplied(true);
    }).catch(() => {});
  }, [pid, user]);

  useEffect(() => {
    if (p?.title) document.title = `${p.title} · ${p.company_name || "SkillConnect"}`;
  }, [p]);

  // Fetch similar jobs (same domain, exclude current)
  useEffect(() => {
    if (!p?.sc_domain) return;
    api.get("/postings", { params: { sc_domain: p.sc_domain } })
      .then(r => setSimilar((r.data || []).filter(x => x.id !== p.id).slice(0, 4)))
      .catch(() => {});
  }, [p]);

  const apply = async () => {
    setApplying(true);
    try {
      await api.post(`/postings/${pid}/apply`);
      setApplied(true);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to apply. Please try again.");
    } finally { setApplying(false); }
  };

  const expDisplay = useMemo(() => {
    if (!p) return null;
    if (p.experience_min != null && p.experience_max != null) return `${p.experience_min}–${p.experience_max} Yrs`;
    if (p.experience_min != null) return `${p.experience_min}+ Yrs`;
    if (p.experience_max != null) return `Up to ${p.experience_max} Yrs`;
    return null;
  }, [p]);

  const share = async () => {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const url = `${base}/api/share/jobs/${pid}`;
    if (navigator.share) {
      try { await navigator.share({ title: p?.title, url }); return; }
      catch { /* user cancelled */ }
    }
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { /* clipboard blocked */ }
  };

  if (err) {
    return (
      <div className="min-h-screen bg-paper">
        <PublicNavBar />
        <main className="max-w-3xl mx-auto px-6 py-20 text-center">
          <h1 className="font-display font-bold text-3xl">Posting unavailable</h1>
          <p className="text-muted mt-2">{err}</p>
          <Link to="/opportunities" className="btn-primary mt-6 inline-flex">Browse all opportunities</Link>
        </main>
        <PublicFooter compact />
      </div>
    );
  }
  if (!p) {
    return (
      <div className="min-h-screen bg-paper">
        <PublicNavBar />
        <main className="max-w-4xl mx-auto px-6 py-20 text-center text-muted">Loading…</main>
        <PublicFooter compact />
      </div>
    );
  }

  const isOther = p.sc_domain === "OTHER";
  const domainColor = SC_TINT[p.sc_domain] || "#0040A8";

  const toc = [
    p.description && { id: "job-description", label: "Job description" },
    (p.skills_required?.length) && { id: "skills", label: "Skills required" },
    (p.perks?.length) && { id: "perks", label: "Perks & benefits" },
    { id: "about-company", label: "About the company" },
    (similar.length > 0) && { id: "similar-jobs", label: "Similar jobs" },
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-paper pb-24 lg:pb-0">
      <PublicNavBar />

      {/* Hero */}
      <section className="bg-white border-b border-rule">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-7">
          <button
            onClick={() => nav(-1)}
            className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-signal transition mb-4"
            data-testid="job-back"
          >
            <ArrowLeft size={14} /> Back to jobs
          </button>

          <div className="flex items-start gap-4 sm:gap-5">
            <CompanyLogo src={p.company_logo} name={p.company_name} />
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-3 flex-wrap">
                <h1
                  className="font-display font-black tracking-tighter text-2xl sm:text-3xl leading-tight"
                  data-testid="job-detail-title"
                >
                  {p.title}
                </h1>
              </div>
              <div className="text-slate-600 mt-1">
                <Building2 size={13} className="inline mr-1 -mt-0.5" />
                {p.company_name || "Company"}
              </div>

              {/* Meta row */}
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-slate-700">
                <span className="inline-flex items-center gap-1.5">
                  <MapPin size={14} className="text-muted" />
                  {p.location || "Remote"}
                </span>
                {expDisplay && (
                  <span className="inline-flex items-center gap-1.5">
                    <Briefcase size={14} className="text-muted" />
                    {expDisplay}
                  </span>
                )}
                {(p.salary_display || p.stipend) && (
                  <span className="inline-flex items-center gap-1.5 font-semibold text-slate-900">
                    <IndianRupee size={14} className="text-muted" />
                    {p.salary_display || p.stipend}
                  </span>
                )}
                {p.openings && (
                  <span className="inline-flex items-center gap-1.5">
                    <Users size={14} className="text-muted" />
                    {p.openings} opening{p.openings > 1 ? "s" : ""}
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5 text-muted">
                  <Clock size={14} />
                  Posted {timeAgo(p.created_at) || "recently"}
                </span>
              </div>

              {/* Badges */}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {p.sc_domain && (
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${isOther ? "border" : "text-white"}`}
                    style={isOther
                      ? { background: "#F1F5F9", color: domainColor, borderColor: domainColor + "55" }
                      : { background: domainColor }}
                  >
                    <ShieldCheck size={9} /> {p.sc_domain_name || p.sc_domain}
                  </span>
                )}
                {p.type && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border border-rule text-slate-600">
                    {p.type}
                  </span>
                )}
                {p.deadline && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                    <Calendar size={11} /> Apply by {new Date(p.deadline).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Body — 2-col grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid lg:grid-cols-[1fr_320px] gap-6">
          <div className="space-y-4">
            {p.description && (
              <Section id="job-description" eyebrow="Job description" testid="section-job-description">
                <p
                  className="text-[15px] leading-relaxed whitespace-pre-wrap text-slate-700"
                  data-testid="job-detail-desc"
                >
                  {p.description}
                </p>
              </Section>
            )}

            {p.skills_required?.length > 0 && (
              <Section id="skills" eyebrow="Skills required" testid="section-skills">
                <div className="flex flex-wrap gap-1.5">
                  {p.skills_required.map((s) => (
                    <span
                      key={s}
                      className="text-xs font-medium px-2.5 py-1 rounded bg-slate-100 text-slate-700 border border-slate-200"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </Section>
            )}

            {p.perks?.length > 0 && (
              <Section id="perks" eyebrow="Perks & benefits" testid="section-perks">
                <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  {p.perks.map((perk) => (
                    <div key={perk} className="flex items-center gap-2 text-slate-700">
                      <span className="h-1.5 w-1.5 rounded-full bg-signal shrink-0" />
                      {perk}
                    </div>
                  ))}
                </div>
              </Section>
            )}

            <Section id="about-company" eyebrow="About the company" testid="section-about-company">
              <div className="flex items-center gap-3">
                <CompanyLogo src={p.company_logo} name={p.company_name} size={56} />
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{p.company_name || "Company"}</div>
                  {p.location && <div className="text-sm text-muted truncate">{p.location}</div>}
                </div>
              </div>
              <Link
                to="/for-companies"
                className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-signal hover:underline"
              >
                Browse all companies →
              </Link>
            </Section>

            {similar.length > 0 && (
              <section
                id="similar-jobs"
                className="scroll-mt-24"
                data-testid="section-similar-jobs"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles size={14} className="text-signal" />
                  <h2 className="font-display font-bold text-lg">Similar jobs</h2>
                </div>
                <div className="space-y-3">
                  {similar.map((s) => (
                    <JobCard key={s.id} p={s} testid={`similar-${s.id}`} />
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Right rail */}
          <StickyApplyPanel
            salary={p.salary_display || p.stipend}
            location={p.location}
            exp={expDisplay}
            openings={p.openings}
            posted={timeAgo(p.created_at)}
            applied={applied}
            applying={applying}
            isStudent={user?.role === "student"}
            onApply={apply}
            onSave={() => setSaved(!saved)}
            saved={saved}
            onShare={share}
            copied={copied}
            toc={toc}
            loginTo="/login"
            loginState={{ next: `/jobs/${p.id}` }}
          />
        </div>
      </main>

      <PublicFooter compact />
    </div>
  );
}
